import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { supabase } from '../lib/supabase';
import { 
  LogOut, 
  FileText, 
  Clock, 
  DollarSign, 
  ArrowLeft, 
  Search, 
  X,
  MessageSquare,
  Bell,
  Settings,
  Code,
  Bot,
  Wand2,
  Database,
  LineChart,
  ShieldCheck,
  Star,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { saveAs } from 'file-saver';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Define TypeScript types for database entities
interface Project {
  id: string;
  client_id: string;
  title: string;
  description?: string;
  status: 'in_progress' | 'completed' | 'cancelled' | 'pending';
  start_date?: string;
  end_date?: string;
  budget?: number;
  created_at: string;
}

interface Review {
  id: string;
  user_id: string;
  profile_id: string;
  rating: number;
  comment?: string;
  created_at: string;
  service_type: string;
  status: 'pending' | 'approved' | 'rejected';
  is_featured: boolean;
}

interface Profile {
  id: string;
  email: string;
  name?: string;
  created_at: string;
}

const services = [
  { icon: <Code className="h-6 w-6" />, name: 'Web Development', color: 'bg-blue-500/10 text-blue-500' },
  { icon: <Bot className="h-6 w-6" />, name: 'AI Agents', color: 'bg-purple-500/10 text-purple-500' },
  { icon: <Wand2 className="h-6 w-6" />, name: 'AI Automation', color: 'bg-pink-500/10 text-pink-500' },
  { icon: <Database className="h-6 w-6" />, name: 'Data Solutions', color: 'bg-green-500/10 text-green-500' },
  { icon: <LineChart className="h-6 w-6" />, name: 'Digital Marketing', color: 'bg-yellow-500/10 text-yellow-500' },
  { icon: <ShieldCheck className="h-6 w-6" />, name: 'Security Solutions', color: 'bg-red-500/10 text-red-500' }
];

const UserDashboard: React.FC = () => {
  const { profile, signOut } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<keyof Project>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [startDate,  ] = useState('');
  const [endDate,  ] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notifications] = useState([
    { id: 1, title: 'Project Update', message: 'New milestone completed for Web App', time: '2h ago' },
    { id: 2, title: 'Message', message: 'New message from your project manager', time: '4h ago' },
    { id: 3, title: 'Invoice', message: 'New invoice available for review', time: '1d ago' }
  ]);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('client_id', profile?.id)
        .order(sortColumn, { ascending: sortDirection === 'asc' });

      if (error) throw error;
      setProjects(data || []);
      setFilteredProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoadingProjects(false);
    }
  }, [profile?.id, sortColumn, sortDirection]);

  const fetchReviews = useCallback(async () => {
    setLoadingReviews(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('profile_id', profile?.id)
        .eq('status', 'approved') // Only show approved reviews
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Failed to load reviews. Please try again later.');
    } finally {
      setLoadingReviews(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) {
      fetchProjects();
      fetchReviews();
    }
  }, [fetchProjects, fetchReviews, profile?.id]);

  useEffect(() => {
    let filtered = [...projects];

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    if (startDate) {
      filtered = filtered.filter(project =>
        project.start_date && new Date(project.start_date) >= new Date(startDate)
      );
    }
    if (endDate) {
      filtered = filtered.filter(project =>
        project.end_date && new Date(project.end_date) <= new Date(endDate)
      );
    }

    setFilteredProjects(filtered);
  }, [searchTerm, statusFilter, startDate, endDate, projects]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleSort = (column: keyof Project) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-500';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-500';
      case 'cancelled':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-yellow-500/10 text-yellow-500';
    }
  };

  const exportToCSV = () => {
    const headers = ['Title,Status,Start Date,End Date,Budget,Description'];
    const rows = filteredProjects.map(project =>
      `"${project.title}","${project.status.replace('_', ' ')}","${
        project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'
      }","${project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}","${
        project.budget?.toLocaleString() ?? '-'
      }","${project.description?.replace(/"/g, '""') || '-'}"`
    );
    const csvContent = headers.concat(rows).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'projects.csv');
  };

  const statusCounts = projects.reduce((acc, project) => {
    acc[project.status] = (acc[project.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const chartData = {
    labels: ['In Progress', 'Completed', 'Cancelled', 'Pending'],
    datasets: [
      {
        label: 'Projects by Status',
        data: [
          statusCounts['in_progress'] || 0,
          statusCounts['completed'] || 0,
          statusCounts['cancelled'] || 0,
          statusCounts['pending'] || 0,
        ],
        backgroundColor: ['#3B82F6', '#22C55E', '#EF4444', '#EAB308'],
        borderColor: ['#2563EB', '#16A34A', '#DC2626', '#CA8A04'],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Project Status Distribution', color: '#ffffff', font: { size: 16 } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { color: '#ffffff' } },
      x: { ticks: { color: '#ffffff' } },
    },
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 text-red-500 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Total Projects</p>
                    <h3 className="text-xl font-bold">
                      {loadingProjects ? 'Loading...' : projects.length}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Active Projects</p>
                    <h3 className="text-xl font-bold">
                      {loadingProjects
                        ? 'Loading...'
                        : projects.filter(p => p.status === 'in_progress').length}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Total Investment</p>
                    <h3 className="text-xl font-bold">
                      {loadingProjects
                        ? 'Loading...'
                        : `$${projects.reduce((sum, p) => sum + (p.budget || 0), 0).toLocaleString()}`}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-8">
              <div className="card p-4">
                <h3 className="text-lg font-bold mb-4">Recent Notifications</h3>
                <div className="space-y-4">
                  {notifications.map(notification => (
                    <div key={notification.id} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                      <Bell className="h-5 w-5 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-medium text-sm">{notification.title}</h4>
                        <p className="text-white/70 text-sm">{notification.message}</p>
                        <span className="text-white/50 text-xs">{notification.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-4">
                <h3 className="text-lg font-bold mb-4">Your Reviews</h3>
                <div className="space-y-4">
                  {loadingReviews ? (
                    <p className="text-white/70 text-sm">Loading reviews...</p>
                  ) : reviews.length > 0 ? (
                    reviews.map(review => (
                      <div key={review.id} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <Star className="h-5 w-5 flex-shrink-0 mt-1 text-yellow-500" />
                        <div>
                          <h4 className="font-medium text-sm">Rating: {review.rating}/5</h4>
                          <p className="text-white/70 text-sm">{review.comment || 'No comment'}</p>
                          <span className="text-white/50 text-xs">
                            Service: {review.service_type} |{' '}
                            {new Date(review.created_at).toLocaleDateString()}
                            {review.is_featured && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500">
                                Featured
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-white/70 text-sm">No approved reviews yet.</p>
                  )}
                </div>
              </div>

              <div className="card p-4">
                <h3 className="text-lg font-bold mb-4">Services Overview</h3>
                <div className="grid grid-cols-2 gap-4">
                  {services.map((service, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${service.color}`}>
                      {service.icon}
                      <span className="text-sm font-medium">{service.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card p-4">
              <h3 className="text-lg font-bold mb-4">Project Analytics</h3>
              <div className="h-64">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          </>
        );

      case 'projects':
        return (
          <div className="card p-4">
            <div className="flex flex-col gap-4 mb-6">
              <h3 className="text-lg font-bold">Project Management</h3>
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm w-full"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm w-full"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                </select>
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors w-full"
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Messages</h3>
              <button className="px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors">
                New Message
              </button>
            </div>
            <div className="text-center text-white/70 py-8">
              Message center coming soon
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Account Settings</h3>
              <button className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors">
                <Settings className="h-4 w-4" />
                Update Settings
              </button>
            </div>
            <div className="text-center text-white/70 py-8">
              Settings panel coming soon
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <nav className="bg-white/5 border-b border-white/10">
        <div className="container-custom py-3 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Home
            </Link>
            <h1 className="text-lg font-bold">Client Dashboard</h1>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <button className="relative">
              <Bell className="h-5 w-5 text-white/70 hover:text-white transition-colors" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full text-xs flex items-center justify-center">
                3
              </span>
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="sm:hidden text-white/70 hover:text-white transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={handleSignOut}
              className="hidden sm:flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div className="sm:hidden bg-white/5 border-t border-white/10 px-4 py-2">
            <div className="flex flex-col gap-2">
              {[
                { id: 'overview', icon: <FileText className="h-4 w-4" />, label: 'Overview' },
                { id: 'projects', icon: <Clock className="h-4 w-4" />, label: 'Projects' },
                { id: 'messages', icon: <MessageSquare className="h-4 w-4" />, label: 'Messages' },
                { id: 'settings', icon: <Settings className="h-4 w-4" />, label: 'Settings' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-3 py-2 text-white/70 hover:text-white transition-colors text-sm"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </nav>

      <div className="container-custom py-6 px-4">
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-2">
            Welcome back, {(profile as unknown as Profile)?.name || (profile as unknown as Profile)?.email?.split('@')[0] || 'Client'}!
          </h2>
          <p className="text-white/70 text-sm">
            Manage your projects and track their progress from your personalized dashboard.
          </p>
        </div>

        <div className="hidden sm:flex flex-wrap gap-2 mb-6">
          {[
            { id: 'overview', icon: <FileText className="h-4 w-4" />, label: 'Overview' },
            { id: 'projects', icon: <Clock className="h-4 w-4" />, label: 'Projects' },
            { id: 'messages', icon: <MessageSquare className="h-4 w-4" />, label: 'Messages' },
            { id: 'settings', icon: <Settings className="h-4 w-4" />, label: 'Settings' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {renderTabContent()}
      </div>

      {selectedProject && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 px-4 overflow-y-auto">
          <div className="card bg-black/95 border border-white/10 w-full max-w-[90vw] mt-8 p-4 relative rounded-lg">
            <button
              onClick={() => setSelectedProject(null)}
              className="absolute top-2 right-2 text-white/70 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-bold mb-3">{selectedProject.title}</h3>
            <p className="text-white/70 mb-4 text-sm">
              {selectedProject.description || 'No description available'}
            </p>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-white/90">Status</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  getStatusColor(selectedProject.status)
                }`}>
                  {selectedProject.status.replace('_', ' ')}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-medium text-white/90">Timeline</h4>
                <p className="text-white/70 text-sm">
                  {selectedProject.start_date ? new Date(selectedProject.start_date).toLocaleDateString() : '-'} to{' '}
                  {selectedProject.end_date ? new Date(selectedProject.end_date).toLocaleDateString() : '-'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-white/90">Budget</h4>
                <p className="text-white/70 text-sm">
                  ${selectedProject.budget?.toLocaleString() ?? '-'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedProject(null)}
              className="mt-4 w-full py-2 bg-white/10 text-white/90 hover:bg-white/20 transition-colors rounded-lg text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;